<template>
  <Loading :loading="calendarLoading" loading-text="Loading calendar...">
    <div class="calendar-shell">
      <div v-if="showProviderSelector" class="provider-toolbar-filter">
        <InviteSelect
          v-model="selectedProviders"
          data-testid="my-calendar.right-calendar.provider-filter-select"
          placeholder="Filter Clinicians"
          :options="providerFilterOptions"
          :multiple="true"
          :collapse-tags="true"
          :max-collapse-tags="1"
          header="Suggested Clinicians"
          class="w-full provider-select calendar-header-select"
        />
      </div>
      <FullCalendar
        ref="calendar"
        data-testid="my-calendar.right-calendar.main-calendar"
        class="main-calendar"
        :options=calendarOptions />
    </div>
  </Loading>
</template>

<script setup>
import { ref, watch, computed, watchEffect, onMounted, onUnmounted } from 'vue';
import moment from 'moment-timezone';
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import momentTimezonePlugin from '@fullcalendar/moment-timezone'
import Loading from '@/NewLibrary/Loading/Loading.vue'
import InviteSelect from '@/views/my-calendar/sections/schedule/schedule-components/InviteSelect.vue'
import { useStore } from 'vuex'

const store = useStore()

const emit = defineEmits(['open-schedule-preview','open-schedule-detail','move-to-date'])

const props = defineProps({
  refresh: {
    type: Boolean,
  },
  moveDate: {
    type: [String, Date], 
    default: () => new Date() 
  }
});

const calendar = ref(null);
const calendarTimezone = computed(() => store.getters['calendar/calendarTimezone'])
const calendarLoading = computed(() => store.getters['calendar/calendarLoading'])
const eventTypeList = computed(() => store.getters['calendar/rawEventTypeList'] || [])
const userInfo = computed(() => store.state.login.userInfo);
const showProviderSelector = computed(() => userInfo.value?.clinic_id === 150105)
const SELECT_ALL_PROVIDER_VALUE = '__all__'
const selectedProviders = ref([])
let providerFetchDebounceTimer = null
let refreshViewTimer = null
let isCalendarUnmounted = false
const hasInitializedProviderSelection = ref(false)

const getSafeCalendarApi = () => {
  if (isCalendarUnmounted || !calendar.value?.getApi) return null

  try {
    return calendar.value.getApi()
  } catch (error) {
    return null
  }
}

const providerOptions = computed(() =>
  (store.state.calendar.rawProviderList || [])
    .filter(({ first_name, last_name, calendar_id }) => (first_name || last_name) && calendar_id)
    .map(({ customer_id, first_name, last_name, calendar_id }) => ({
      id: customer_id,
      name: `${first_name || ''} ${last_name || ''}`.trim(),
      email: '',
      calendar_id,
    }))
)

const allProviderIds = computed(() => providerOptions.value.map(provider => provider.id))

const selectedProviderCalendarIds = computed(() => {
  const selectedIds = selectedProviders.value.filter(value => value !== SELECT_ALL_PROVIDER_VALUE)
  return providerOptions.value
    .filter(provider => selectedIds.includes(provider.id))
    .map(provider => provider.calendar_id)
})

const providerFilterOptions = computed(() => {
  if (!showProviderSelector.value) return []
  return [
    {
      id: SELECT_ALL_PROVIDER_VALUE,
      name: 'Select All',
      email: '',
    },
    ...providerOptions.value,
  ]
})

const normalizeSelectedProviders = (values = [], oldValues = []) => {
  const uniqueValues = Array.from(new Set(values))
  const uniqueOldValues = Array.from(new Set(oldValues || []))
  const providerIds = uniqueValues.filter(value => value !== SELECT_ALL_PROVIDER_VALUE)
  const oldProviderIds = uniqueOldValues.filter(value => value !== SELECT_ALL_PROVIDER_VALUE)
  const hasSelectAll = uniqueValues.includes(SELECT_ALL_PROVIDER_VALUE)
  const hadSelectAll = uniqueOldValues.includes(SELECT_ALL_PROVIDER_VALUE)
  const allSelected = providerIds.length === allProviderIds.value.length && allProviderIds.value.length > 0

  if (hasSelectAll && !hadSelectAll) {
    return [SELECT_ALL_PROVIDER_VALUE, ...allProviderIds.value]
  }

  if (!hasSelectAll && hadSelectAll) {
    if (providerIds.length === oldProviderIds.length - 1) {
      return providerIds
    }

    return []
  }

  if (allSelected) {
    return [SELECT_ALL_PROVIDER_VALUE, ...allProviderIds.value]
  }

  return providerIds
}

const fetchEventsBySelectedProviders = async (calendarId) => {
  if (!calendarId) return

  const isClinic150105 = userInfo.value?.clinic_id === 150105;
  const calendarIds = isClinic150105
    ? selectedProviderCalendarIds.value
    : [calendarId]

  if (isClinic150105 && calendarIds.length === 0) {
    if (!hasInitializedProviderSelection.value || providerOptions.value.length === 0) {
      return
    }
    store.commit('calendar/SET_RAW_EVENT_LIST', [])
    return
  }

  const getEventsInput = {
    calendar_ids: calendarIds,
    start_time: moment.utc('1970-01-01').startOf('day').toISOString(),
    end_time: moment().add(2, 'years').endOf('day').toISOString()
  }
  store.commit('calendar/SET_CALENDAR_LOADING', true);
  await store.dispatch('calendar/fetchEvents', getEventsInput)
  store.commit('calendar/SET_CALENDAR_LOADING', false);
}

// FullCalendar already stamps day cells / headers with data-date="YYYY-MM-DD" in the
// calendar's timezone; fall back to formatting the Date when it is absent.
const dateKeyOf = (info) => info.el.getAttribute('data-date')
  || moment(info.date).tz(calendarTimezone.value).format('YYYY-MM-DD')

// FullCalendar toolbar buttons (prev / next / today / month / week / day) and the
// title are rendered by the library, so tag them once the view is mounted.
const TOOLBAR_TESTIDS = {
  'fc-prev-button': 'prev-btn',
  'fc-next-button': 'next-btn',
  'fc-today-button': 'today-btn',
  'fc-dayGridMonth-button': 'month-view-btn',
  'fc-timeGridWeek-button': 'week-view-btn',
  'fc-timeGridDay-button': 'day-view-btn',
  'fc-toolbar-title': 'toolbar-title',
}
const tagCalendarToolbar = (calendarEl) => {
  if (!calendarEl) return
  Object.entries(TOOLBAR_TESTIDS).forEach(([className, testId]) => {
    const el = calendarEl.querySelector(`.${className}`)
    if (el) el.setAttribute('data-testid', `my-calendar.right-calendar.${testId}`)
  })
}

const getEventBackgroundColor = (event) => {
  const typeName = event?.event_type
  const matched = eventTypeList.value.find(t => t?.name === typeName)
  if (matched?.color_hex) return matched.color_hex

  return '#E1F5FE'
}

const convertedEventList = computed(() => {
  const eventList = [...store.state.calendar.rawEventList];
  return eventList.filter(event => !event?.is_canceled).map(event => {
    return {
      ...event,
      start: event.start_time,
      end: event.end_time,
      backgroundColor: getEventBackgroundColor(event),
      title: event.event_title,
    }
  })
});

const calendarOptions = ref({
  // dayHeaderContent: renderCustomAxis,
  plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, momentTimezonePlugin],
  height: '100%',
  initialView: 'timeGridWeek', // default view
  // headerToolbar: false,
  headerToolbar: {
    left: 'prev next today', 
    center: 'title',
    right: 'dayGridMonth timeGridWeek timeGridDay',
  }, 
  buttonText: {
    today: 'Today',
    month: 'Month',
    week: 'Week',
    day: 'Day',
  },
  timeZone: calendarTimezone.value,
  weekends: true,
  selectable: true,
  editable: true,
  droppable: true,
  slotDuration: '00:15:00',
  slotMinTime: '00:00:00', 
  slotMaxTime: '24:00:00',
  scrollTime: '07:00:00',

  select: function(info) {
    const startDate = moment(info.start).tz(calendarTimezone.value);
    const endDate = moment(info.end).tz(calendarTimezone.value);
    const duration = moment.duration(endDate.diff(startDate)).asHours();

    if (duration > 24) {
      alert('The selected time range cannot exceed 24 hours');
      calendar.value.getApi().unselect(); 
      return;
    }
    let startTimeFormatted = startDate.format('hh:mm A');
    let endTimeFormatted = endDate.format('hh:mm A');
    if (startDate.hour() === 0 && endDate.hour() === 0 ) {
      startTimeFormatted = '07:00 AM';
      endTimeFormatted = '07:30 AM';
    }

    emit('open-schedule-preview', {
      ...info,
      x: info.jsEvent?.clientX,
      y: info.jsEvent?.clientY
    });
    
  },
  selectAllow: function(selectInfo) {
// check whether includes .fc-day-other class
    const start = selectInfo.start;
    const end = selectInfo.end;
    let isValid = true;
    let date = start;
    while (date < end) {
      const dayEl = document.querySelector(`[data-date='${date.toISOString().split('T')[0]}']`);
      if (dayEl && dayEl.classList.contains('fc-day-other')) {
        isValid = false;
        break;
      }
      date.setDate(date.getDate() + 1); // 
    }
    return isValid;
  },
  events: convertedEventList.value,

  // FullCalendar owns the toolbar / grid DOM, so testids for those are attached via
  // its mount hooks instead of the template. Keys use the record identifier (date or
  // event_id) so every cell / entry is addressable.
  viewDidMount: function(info) {
    tagCalendarToolbar(info.el.closest('.fc'));
  },
  dayHeaderDidMount: function(info) {
    info.el.setAttribute('data-testid', `my-calendar.right-calendar.day-header.${dateKeyOf(info)}`);
  },
  dayCellDidMount: function(info) {
    info.el.setAttribute('data-testid', `my-calendar.right-calendar.day-cell.${dateKeyOf(info)}`);
  },

  eventDidMount: function(info) {
    info.el.setAttribute('data-testid', `my-calendar.right-calendar.event.${info.event.extendedProps.event_id}`);
    // Apply custom styles to the event element
    info.el.style.backgroundColor = info.event.backgroundColor || '#20A7C1';
    info.el.style.color = '#000'; // Set text color
    info.el.style.borderWidth = '0px'; // Set border width as needed
    info.el.style.borderRadius = '2px'
  },

  
  // todo
  eventClick: function(info) {
    let oldEvent = convertedEventList.value.find(event => event.event_id === info.event.extendedProps.event_id)
    let newEvent = {
      ...oldEvent,
      meetingType: info.event.extendedProps.event_type,
      meetingUrl: info.event.extendedProps.external_url,
      mode: 'edit',
      preview_start: info.event.start,
      preview_end: info.event.end,
      x: info.jsEvent?.clientX,
      y: info.jsEvent?.clientY,
      jsEvent: info.jsEvent
    }
    emit('open-schedule-preview',newEvent)

  },
  eventDrop: async function(info) {
    // console.log(info,'eventDrop')
    let oldEvent = convertedEventList.value.find(event => event.event_id === info.event.extendedProps.event_id)
    let newEvent = {
      ...oldEvent,
      meetingType: info.event.extendedProps.event_type,
      meetingUrl: info.event.extendedProps.external_url,
      mode: 'edit',
      preview_start: info.event.start,
      preview_end: info.event.end,
    }
    emit('open-schedule-detail',newEvent)
    // console.log(oldEvent,'oldEvent')
  },
  eventResize: async function(info) {
    let oldEvent = convertedEventList.value.find(event => event.event_id === info.event.extendedProps.event_id)
    let newEvent = {
      ...oldEvent,
      meetingType: info.event.extendedProps.event_type,
      meetingUrl: info.event.extendedProps.external_url,
      mode: 'edit',
      preview_start: info.event.start,
      preview_end: info.event.end,
    }
    emit('open-schedule-detail',newEvent)   

  },

  eventContent: function(arg) {
    // Create custom HTML structure
    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.flexDirection = 'column'; // Column layout
    container.style.justifyContent = 'center'; // Left align horizontally
    container.style.alignItems = 'flex-start'
    container.style.padding = '0 0 0 3px'; // Column layout
    container.style.height = '100%';
    container.style.width = '100%'; // Ensure container takes full width
    container.style.color = '#263238'; // event title olor
    container.style.boxSizing = 'border-box';
    
    const eventTime = document.createElement('div');
    const start = moment(arg.event.start).tz(calendarTimezone.value).format('HH:mm');
    const end = moment(arg.event.end).tz(calendarTimezone.value).format('HH:mm');
    const duration = moment.duration(moment(arg.event.end).diff(moment(arg.event.start)));
    const isShortEvent = duration.asHours() < 1;

    const eventTitle = document.createElement('div');
    eventTitle.innerHTML = arg.event.title;
    eventTitle.style.width = '100%';
    eventTitle.style.textAlign = 'left'; // Left align text
    eventTitle.style.whiteSpace = 'nowrap'; // Prevent wrapping
    eventTitle.style.overflow = 'hidden'; // Hide overflow text
    eventTitle.style.textOverflow = 'ellipsis';

    if (isShortEvent) {
      // If event is less than 1 hour, put time and title on one line
      eventTitle.innerHTML = `${arg.event.title} ${start} - ${end}`;

      container.appendChild(eventTitle);
    } else {
      // If event is 1 hour or more, separate time and title
      eventTime.innerHTML = `${start} - ${end}`;
      eventTime.style.textAlign = 'left'; // Left align text
      eventTitle.style.marginBottom = '3px'; // Add margin bottom for spacing

      container.appendChild(eventTitle);
      container.appendChild(eventTime);
    }

    return { domNodes: [container] };
  },
  views: {
    dayGridMonth: { // month view settings
      eventTimeFormat: { // like '7pm'
        hour: 'numeric',
        minute: '2-digit',
        omitZeroMinute: true,
        meridiem: 'short'
      },
      eventContent: function(arg) {
        // Create custom HTML structure
        const container = document.createElement('div');
        container.style.display = 'flex';
        container.style.alignItems = 'center'; // Left align horizontally
        container.style.padding = '0 1px 0 6px'; // Padding
        container.style.height = '100%';
        container.style.width = '100%'; // Ensure container takes full width
        container.style.boxSizing = 'border-box'; // Include padding in width


        const colorCircle = document.createElement('div');
        colorCircle.style.width = '12px';
        colorCircle.style.height = '12px';
        colorCircle.style.borderRadius = '50%';
        colorCircle.style.backgroundColor = arg.event.backgroundColor || '#20A7C1'; // Use event's background color or default blue
        colorCircle.style.marginRight = '6px';
        colorCircle.style.flexShrink = '0';

        const eventTime = document.createElement('div');
        eventTime.textContent = moment(arg.event.start).tz(calendarTimezone.value).format('h:mma');
        eventTime.style.marginRight = '5px';

        const eventTitle = document.createElement('div');
        eventTitle.textContent = arg.event.title;
        eventTitle.style.width = '100%'; // Ensure title takes full width
        eventTitle.style.textAlign = 'left'; // Left align text
        eventTitle.style.whiteSpace = 'nowrap'; // Prevent wrapping
        eventTitle.style.overflow = 'hidden'; // Hide overflow text
        eventTitle.style.textOverflow = 'ellipsis'; // Add ellipsis for overflow
        eventTitle.style.fontWeight = '600';

        container.appendChild(colorCircle);
        container.appendChild(eventTime);
        container.appendChild(eventTitle);

        return { domNodes: [container] };
      },
      eventDidMount: function(info) {
        info.el.setAttribute('data-testid', `my-calendar.right-calendar.event.${info.event.extendedProps.event_id}`);
        // Apply custom styles to the event element
        // info.el.style.backgroundColor = info.event.backgroundColor;
        // const textColor = info.event.textColor || 'inherit'; // Get textColor from event props
        // info.el.style.color = textColor; // Set text color
        // info.el.style.borderColor = textColor; 
        // info.el.style.borderWidth = '0px'; // Set border width as needed
        // info.el.style.borderStyle = 'solid'; // Set border style as needed
        // info.el.style.borderRadius = '2px'
      },
    }
  },


  dateClick: function(info) {
    const dayEl = info.dayEl;
    if (dayEl.classList.contains('fc-day-other')) {
      // prevent click event
      info.jsEvent.preventDefault();
    } else {


      // console.log('Date clicked:', moment(info.date).format('YYYY-MM-DD'));
    }

  },
  datesSet: function(dateInfo) {
    const viewType = dateInfo.view.type;
    let dateToEmit;

    if (viewType === 'timeGridWeek') {
      // Week view: send the entire week range
      const weekStart = moment(dateInfo.view.currentStart).tz(calendarTimezone.value);
      const weekEnd = moment(dateInfo.view.currentEnd).tz(calendarTimezone.value).subtract(1, 'day');
      
      dateToEmit = {
        type: 'week',
        start: weekStart.format('YYYY-MM-DD'),
        end: weekEnd.format('YYYY-MM-DD')
      };
    } else if (viewType === 'timeGridDay') {
      // Day view: send the specific date
      const dayDate = moment(dateInfo.view.currentStart).tz(calendarTimezone.value);
      
      dateToEmit = {
        type: 'day',
        date: dayDate.format('YYYY-MM-DD')
      };
    } else if (viewType === 'dayGridMonth') {
      // Month view: send the first day of the month
      const monthStart = moment(dateInfo.view.currentStart).tz(calendarTimezone.value).startOf('month');
      
      dateToEmit = {
        type: 'month',
        date: monthStart.format('YYYY-MM-DD')
      };
    }

    emit('move-to-date', dateToEmit);
  },
});


// Watch for clinic changes and update calendar options
watchEffect(() => {
  const calendarApi = getSafeCalendarApi()
  if (!calendarApi) return

  calendarApi.setOption('editable', true);
  calendarApi.setOption('droppable', true);
  calendarApi.setOption('timeZone', calendarTimezone.value);
});

watch(() => props.refresh, async () => {
  if (showProviderSelector.value) {
    await fetchEventsBySelectedProviders(store.state.calendar.creatorCalendarId)
  }

  const calendarApi = getSafeCalendarApi()
  if (!calendarApi) return

  clearTimeout(refreshViewTimer)
  refreshViewTimer = setTimeout(() => {
    const safeCalendarApi = getSafeCalendarApi()
    if (!safeCalendarApi) return

    const currentView = safeCalendarApi.view;
    const viewType = currentView.type;
    const currentDate = currentView.currentStart;
    safeCalendarApi.changeView(viewType, currentDate);
  }, 500);
});

watch(() => props.moveDate, () => {
  const calendarApi = getSafeCalendarApi()
  if (!calendarApi) return

  calendarApi.gotoDate(props.moveDate);
});

watch(convertedEventList, (newEvents) => {
  const calendarApi = getSafeCalendarApi()
  if (!calendarApi) return

  calendarApi.removeAllEvents()
  calendarApi.addEventSource(newEvents)
}, { deep: true })

watch(
  () => store.state.calendar.creatorCalendarId,
  async (calendarId) => {
    if (calendarId) {
      await fetchEventsBySelectedProviders(calendarId)
    }
  },
  { immediate: true }
)

watch(providerOptions, (newOptions) => {
  if (!showProviderSelector.value || !newOptions.length || selectedProviders.value.length) return
  const currentProvider = newOptions.find(provider => String(provider.id) === String(userInfo.value?.customer_id))
  selectedProviders.value = currentProvider ? [currentProvider.id] : []
  hasInitializedProviderSelection.value = true
}, { immediate: true })

watch(selectedProviderCalendarIds, (newCalendarIds) => {
  store.commit('calendar/SET_SELECTED_CLINICIAN_CALENDAR_IDS', newCalendarIds)
}, { immediate: true })

watch(selectedProviders, (newVal, oldVal) => {
  if (!showProviderSelector.value) return

  const normalized = normalizeSelectedProviders(newVal, oldVal || [])
  const oldNormalized = normalizeSelectedProviders(oldVal || [], oldVal || [])

  if (JSON.stringify(normalized) !== JSON.stringify(newVal)) {
    selectedProviders.value = normalized
    return
  }

  hasInitializedProviderSelection.value = true

  if (JSON.stringify(normalized) === JSON.stringify(oldNormalized)) {
    return
  }

  clearTimeout(providerFetchDebounceTimer)
  providerFetchDebounceTimer = setTimeout(async () => {
    await fetchEventsBySelectedProviders(store.state.calendar.creatorCalendarId)
  }, 300)
}, { deep: true })

onUnmounted(() => {
  isCalendarUnmounted = true
  clearTimeout(providerFetchDebounceTimer)
  clearTimeout(refreshViewTimer)
})
</script>

<style scoped>
.calendar-shell {
  position: relative;
}

.provider-toolbar-filter {
  position: absolute;
  top: 25px;
  right: 238px;
  width: 180px;
  z-index: 12;
}

.provider-toolbar-filter :deep(.provider-select .el-select__wrapper),
.provider-toolbar-filter :deep(.provider-select .el-select-v2__wrapper) {
  min-height: 36px;
}
</style>
